import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * A `<label for={id}>` only labels form controls, and the segmented field is a group of
 * spinbuttons. This finds the labels pointing at `id` (shadcn's `FormLabel` among them), names
 * the group with them through `aria-labelledby`, and makes a click on one focus the field, as it
 * focuses a native input. Returns the ids for `aria-labelledby`.
 */
export function useLabelFor(
  id: string | undefined,
  groupRef: RefObject<HTMLElement | null>,
  onLabelClick: () => void,
): string | undefined {
  const [labelIds, setLabelIds] = useState<string | undefined>(undefined);
  const onClickRef = useRef(onLabelClick);
  useEffect(() => {
    onClickRef.current = onLabelClick;
  });

  useEffect(() => {
    const group = groupRef.current;
    if (!id || !group) return;
    const root = group.getRootNode() as Document | ShadowRoot;
    const labels = Array.from(root.querySelectorAll('label')).filter(
      (label) => label.htmlFor === id,
    );
    labels.forEach((label, index) => {
      if (!label.id) label.id = `${id}-label${index === 0 ? '' : `-${index}`}`;
    });
    setLabelIds(labels.length > 0 ? labels.map((label) => label.id).join(' ') : undefined);
    const onClick = (event: MouseEvent) => {
      event.preventDefault();
      onClickRef.current();
    };
    labels.forEach((label) => label.addEventListener('click', onClick));
    return () => labels.forEach((label) => label.removeEventListener('click', onClick));
  }, [id, groupRef]);

  return labelIds;
}
